/**
 * <article-search> – blog sidebar search (sections/article-detail.liquid).
 * Suggests blog post titles while typing using Shopify's predictive search API
 * (articles only, matched on title). Clicking a suggestion opens that post;
 * Enter without a highlighted suggestion submits to the search results page.
 */
if (!customElements.get('article-search')) {
  class ArticleSearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('.article-search__input');
      this.list = this.querySelector('.article-search__results');
      this.status = this.querySelector('[data-status]');
      if (!this.input || !this.list) return;

      this.activeIndex = -1;
      this.abortController = null;
      this.debounceTimer = null;
      this.listeners = new AbortController();
      const { signal } = this.listeners;

      this.input.addEventListener('input', () => this.onInput(), { signal });
      this.input.addEventListener('keydown', (event) => this.onKeydown(event), { signal });
      this.input.addEventListener('focus', () => this.list.children.length && this.open(), { signal });
      document.addEventListener(
        'click',
        (event) => {
          if (!this.contains(event.target)) this.close();
        },
        { signal }
      );
    }

    disconnectedCallback() {
      this.listeners?.abort();
      this.abortController?.abort();
      clearTimeout(this.debounceTimer);
    }

    onInput() {
      clearTimeout(this.debounceTimer);
      const term = this.input.value.trim();
      if (term.length < 2) {
        this.render([]);
        return;
      }
      this.debounceTimer = setTimeout(() => this.fetchSuggestions(term), 200);
    }

    async fetchSuggestions(term) {
      this.abortController?.abort();
      this.abortController = new AbortController();

      const url = new URL(this.dataset.suggestUrl, window.location.origin);
      url.searchParams.set('q', term);
      url.searchParams.set('resources[type]', 'article');
      url.searchParams.set('resources[limit]', this.dataset.limit || '6');
      url.searchParams.set('resources[options][fields]', 'title');

      try {
        const response = await fetch(url, {
          signal: this.abortController.signal,
          headers: { Accept: 'application/json' },
        });
        if (!response.ok) throw new Error(`Search failed: ${response.status}`);
        const data = await response.json();
        if (this.input.value.trim() !== term) return;
        this.render(data?.resources?.results?.articles ?? []);
      } catch (error) {
        if (error.name !== 'AbortError') this.render([]);
      }
    }

    render(articles) {
      this.activeIndex = -1;
      this.list.replaceChildren();

      for (const [index, article] of articles.entries()) {
        const item = document.createElement('li');
        item.setAttribute('role', 'option');
        item.id = `${this.list.id}-${index}`;
        const link = document.createElement('a');
        link.href = article.url;
        link.textContent = article.title;
        link.tabIndex = -1;
        item.append(link);
        this.list.append(item);
      }

      const term = this.input.value.trim();
      if (articles.length) {
        this.open();
        this.setStatus(`${articles.length} suggestion${articles.length === 1 ? '' : 's'} available`);
      } else if (term.length >= 2) {
        const empty = document.createElement('li');
        empty.className = 'article-search__empty';
        empty.textContent = 'No articles found';
        this.list.append(empty);
        this.open();
        this.setStatus('No articles found');
      } else {
        this.close();
      }
    }

    onKeydown(event) {
      const options = [...this.list.querySelectorAll('[role="option"]')];
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        if (!options.length) return;
        event.preventDefault();
        const step = event.key === 'ArrowDown' ? 1 : -1;
        this.activeIndex = (this.activeIndex + step + options.length) % options.length;
        this.highlight(options);
      } else if (event.key === 'Enter' && this.activeIndex > -1 && options[this.activeIndex]) {
        event.preventDefault();
        window.location.href = options[this.activeIndex].querySelector('a').href;
      } else if (event.key === 'Escape') {
        this.close();
      }
    }

    highlight(options) {
      options.forEach((option, index) => option.setAttribute('aria-selected', String(index === this.activeIndex)));
      const active = options[this.activeIndex];
      if (active) {
        this.input.setAttribute('aria-activedescendant', active.id);
        active.scrollIntoView({ block: 'nearest' });
      }
    }

    open() {
      this.list.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
    }

    close() {
      this.list.hidden = true;
      this.input.setAttribute('aria-expanded', 'false');
      this.input.removeAttribute('aria-activedescendant');
      this.activeIndex = -1;
    }

    setStatus(message) {
      if (this.status) this.status.textContent = message;
    }
  }

  customElements.define('article-search', ArticleSearch);
}
